'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';
import { Input } from '@/src/shared/ui/input';
import { Label } from '@/src/shared/ui/label';

import { resetPasswordSchema } from '../model/schemas';
import type { ResetPasswordFormValues, ResetPasswordState } from '../model/types';
import { resetPasswordAction } from '../api/resetPasswordAction';
import { InvalidResetLinkPanel } from './InvalidResetLinkPanel';
import {
  getNewPasswordErrorMessage,
  getConfirmPasswordErrorMessage,
  getResetApiErrorMessage,
} from './mapResetPasswordFieldError';

type Props = {
  token: string;
};

export function ResetPasswordForm({ token }: Props) {
  const t = useTranslations('auth.resetPassword');
  const params = useParams();
  const router = useRouter();
  const locale = (params.locale as string) ?? 'es';

  const [state, setState] = useState<ResetPasswordState>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({ resolver: yupResolver(resetPasswordSchema) });

  const newPasswordError = getNewPasswordErrorMessage(t, errors.new_password?.message);
  const confirmPasswordError = getConfirmPasswordErrorMessage(t, errors.confirm_password?.message);

  async function onSubmit(data: ResetPasswordFormValues) {
    setState('submitting');

    let result;
    try {
      result = await resetPasswordAction(data, token);
    } catch {
      // Only genuinely unexpected throws land here; business failures return.
      toast.error(getResetApiErrorMessage(t, 'server_error'));
      setState('error');
      return;
    }

    if (result && !result.ok) {
      // A dead link has no recovery except the panel offering a new one.
      if (result.code === 'invalid_token') {
        setState('invalid_token');
        return;
      }
      toast.error(getResetApiErrorMessage(t, result.code));
      setState('error');
      return;
    }

    toast.success(t('success'));
    router.push(`/${locale}/login`);
  }

  if (!token || state === 'invalid_token') {
    return <InvalidResetLinkPanel locale={locale} />;
  }

  const submitting = state === 'submitting';

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reset-new-password" className="text-xs font-medium text-muted-foreground">
          {t('fields.new_password.label')}
        </Label>
        <Input
          id="reset-new-password"
          type="password"
          autoComplete="new-password"
          placeholder={t('fields.new_password.placeholder')}
          aria-invalid={!!newPasswordError}
          aria-describedby={newPasswordError ? 'reset-new-password-error' : undefined}
          className="h-11 border-border/80 bg-background"
          {...register('new_password')}
        />
        {newPasswordError && (
          <p id="reset-new-password-error" role="alert" className="text-xs text-destructive">
            {newPasswordError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="reset-confirm-password"
          className="text-xs font-medium text-muted-foreground"
        >
          {t('fields.confirm_password.label')}
        </Label>
        <Input
          id="reset-confirm-password"
          type="password"
          autoComplete="new-password"
          placeholder={t('fields.confirm_password.placeholder')}
          aria-invalid={!!confirmPasswordError}
          aria-describedby={confirmPasswordError ? 'reset-confirm-password-error' : undefined}
          className="h-11 border-border/80 bg-background"
          {...register('confirm_password')}
        />
        {confirmPasswordError && (
          <p id="reset-confirm-password-error" role="alert" className="text-xs text-destructive">
            {confirmPasswordError}
          </p>
        )}
      </div>

      <Button type="submit" disabled={submitting} className="h-11 w-full rounded-lg text-[14px]">
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>
    </form>
  );
}
